import type { FC, ReactNode } from 'react'

import { useMemo } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import NeonBoard from '@/components/chess/neon-board'
import NeonPiece from '@/components/chess/neon-piece'
import SectionHeading from '@/components/landing/section-heading'
import { useBoardTheme } from '@/components/chess/board-theme'
import { piecesFromFen } from '@/lib/chess'

const PREVIEW_FEN = 'r1bqkb1r/pppp1Qpp/2n2n2/4p3/2B1P3/8/PPPP1PPP/RNB1K1NR b KQkq - 0 4'

const ThemesSection: FC = (): ReactNode => {
    const { theme, themes, setThemeId } = useBoardTheme()
    const previewPieces = useMemo(() => piecesFromFen(PREVIEW_FEN), [])

    return (
        <section id='themes' aria-labelledby='themes-title' className='relative scroll-mt-20 py-24 sm:py-32'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10 overflow-hidden'>
                <div
                    className='absolute -right-40 top-1/3 size-[30rem] rounded-full opacity-25 blur-[130px] transition-colors duration-700'
                    style={{ backgroundColor: theme.white }}
                />
                <div
                    className='absolute -left-40 bottom-0 size-[26rem] rounded-full opacity-20 blur-[130px] transition-colors duration-700'
                    style={{ backgroundColor: theme.black }}
                />
            </div>

            <div className='mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.05fr] lg:gap-16'>
                <div>
                    <SectionHeading
                        id='themes-title'
                        eyebrow='Board themes'
                        accent='lime'
                        title='Pick your glow.'
                        description="Four hand-tuned neon sets, each built to keep checks, captures and last moves readable at a glance. Choose one here and it lights up every board on this page."
                    />

                    <fieldset className='mt-10'>
                        <legend className='sr-only'>
                            Board theme
                        </legend>

                        <div className='grid gap-3 sm:grid-cols-2'>
                            {themes.map(option => {
                                const active = option.id === theme.id

                                return (
                                    <label
                                        key={option.id}
                                        className={cn(
                                            'group relative flex cursor-pointer flex-col rounded-2xl border bg-card/50 p-4 transition-all duration-300 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-neon-cyan',
                                            active ? 'bg-card' : 'border-white/10 hover:border-white/25'
                                        )}
                                        style={active ? { borderColor: `${option.white}99`, boxShadow: `0 0 0 1px ${option.white}40, 0 0 26px ${option.white}33` } : undefined}
                                    >
                                        <input
                                            type='radio'
                                            name='board-theme'
                                            value={option.id}
                                            checked={active}
                                            onChange={() => setThemeId(option.id)}
                                            className='sr-only'
                                        />

                                        <span className='flex items-center justify-between gap-3'>
                                            <span className='flex items-center gap-1'>
                                                <span className='flex size-8 items-center justify-center rounded-lg' style={{ backgroundColor: option.light, color: option.white }}>
                                                    <NeonPiece type='n' strokeWidth={2} className='size-7' />
                                                </span>

                                                <span className='flex size-8 items-center justify-center rounded-lg' style={{ backgroundColor: option.dark, color: option.black }}>
                                                    <NeonPiece type='n' strokeWidth={2} className='size-7 -scale-x-100' />
                                                </span>
                                            </span>

                                            <span
                                                className={cn(
                                                    'flex size-6 items-center justify-center rounded-full border transition-colors',
                                                    active ? 'border-transparent text-primary-foreground' : 'border-white/20 text-transparent'
                                                )}
                                                style={active ? { backgroundColor: option.white } : undefined}
                                            >
                                                <Check className='size-3.5' />
                                            </span>
                                        </span>

                                        <span className='mt-4 font-heading text-base font-semibold tracking-tight' style={{ color: active ? option.white : undefined }}>
                                            {option.name}
                                        </span>

                                        <span className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                            {option.tagline}
                                        </span>
                                    </label>
                                )
                            })}
                        </div>
                    </fieldset>
                </div>

                <figure className='mx-auto w-full max-w-[32rem] lg:mr-0'>
                    <NeonBoard
                        pieces={previewPieces}
                        theme={theme}
                        label={`${theme.name} board theme preview showing a checkmate`}
                        lastMove={{ from: 'h5', to: 'f7' }}
                        checkSquare='e8'
                    />

                    <figcaption className='mt-5 text-center font-mono text-xs text-muted-foreground'>
                        <span style={{ color: theme.white }}>{theme.name}</span>
                        {' · '}
                        Scholar&apos;s mate, four moves in
                    </figcaption>
                </figure>
            </div>
        </section>
    )
}

export default ThemesSection
